import { Back, Expo, Linear, TimelineLite, TweenLite } from "gsap";
import * as HLF from "html-living-framework";

import "blueimp-canvas-to-blob";
import {saveAs} from "file-saver";
import * as printJS from "print-js";

import { Application } from "../Application";
import * as Constants from "../Data/Constants";
import * as SFX from "../Data/SoundEffect";
import * as VO from "../Data/VoiceOver";

import { Button } from "../GameObjects/Button";
import { KeyboardFocusManager } from "../GameObjects/KeyboardFocusManager";
import { InteruptType, VoiceCategory } from "../GameObjects/VoiceOverManager";

import { DropShadowFilter } from "../vendor/filters/DropShadowFilter";
import { ZoomBlurFilter } from "../vendor/filters/ZoomBlurFilter";

import { Game } from "./Game";

const inGamesApp = /packageName=org\.pbskids\.gamesapp/.test(window.location.search);

/**
 * @example stateManager.registerState(AfterGame.ID, AfterGame.Create);
 * @example HLF.AssetMap.addAssetGroup(AfterGame.ID, AfterGame.assets);
 */
export class AfterGame extends HLF.State {

    public static readonly ID = "AFTER_GAME";
    public static readonly assets = [
        new HLF.AssetMapData("wall", HLF.AssetType.PNG),
        new HLF.AssetMapData("luna_carmen-printable-horizontal-colour", HLF.AssetType.PNG),
        new HLF.AssetMapData(Constants.SPRITESHEET_UI, HLF.AssetType.TEXTURE_ATLAS),
        new HLF.AssetMapData(Constants.FONT_LUNA, HLF.AssetType.FONT),
        new HLF.AssetMapAudioData(SFX.CHEER_START, HLF.AssetType.AUDIO, HLF.AudioCategory.SFX),
        new HLF.AssetMapAudioData(SFX.CHEER_LOOP, HLF.AssetType.AUDIO, HLF.AudioCategory.SFX),
    ];
    protected readonly _keyboardFocusManager = this.addChild(new KeyboardFocusManager());
    private readonly _bg = this.addChild(HLF.PixiUtils.makeSprite("wall"));
    private readonly photoContainer = this.addChild(new PIXI.Container());
    private readonly photo: PIXI.Sprite;
    private canvas?: HTMLCanvasElement;
    private readonly filter: ZoomBlurFilter;
    private readonly continueButton = this.addChild(new Button(
        this.continue.bind(this),
        "button-continue",
        Constants.SPRITESHEET_UI,
        VO.U3,
    ));

    private readonly downloadButton = this.addChild(new Button(
        this.download.bind(this), "button-download", Constants.SPRITESHEET_UI, VO.U17));
    private readonly printButton = this.addChild(new Button(
        this.print.bind(this), "button-print", Constants.SPRITESHEET_UI));
    private _blob?: Blob | null;

    constructor() {
        super();

        this.continueButton.name = "Continue";
        this.printButton.name = "Print";
        this.downloadButton.name = "Download";

        this._bg.alpha = 0;
        this._bg.anchor.set(0.5);

        const whitebg = new PIXI.Sprite(PIXI.Texture.WHITE);
        this.photoContainer.addChild(whitebg);
        whitebg.anchor.set(0.5);
        whitebg.position.set(0, 0);

        this.photo = new PIXI.Sprite(Game.screenshot);
        this.photoContainer.addChild(this.photo);
        this.photo.scale.set(0.8853);
        this.photo.anchor.set(0.5);
        this.photoContainer.rotation = PIXI.DEG_TO_RAD * -2.03;

        whitebg.width = this.photo.width + 40;
        whitebg.height = this.photo.height + 40;

        this.filter = new ZoomBlurFilter(0.5);
        this.photo.filters = [this.filter];
        /* const text = this.photoContainer.addChild(
            new PIXI.extras.BitmapText("My Concert", {
                font: Constants.FONT_LUNA,
                tint: HLF.Colour.BLACK.toHex(),
                // align: "right",
            }),
        );
        text.anchor = new PIXI.Point(1, 1);
        text.position.set(this.photo.width / 2 - 30, this.photo.height / 2 - 30);
 */
        this.photoContainer.filters = [new DropShadowFilter({
            rotation: 119,
            distance: 12,
            color: 0x000000,
            alpha: 0.19,
            blur: 2.45,
        })];

        this.setChildIndex(this._keyboardFocusManager, this.children.length - 1);

        if (inGamesApp) {
            this.downloadButton.visible = false;
            this.printButton.visible = false;
        }
        else {
            this.canvas = this.prepareCanvas();
            this.downloadButton.interactive = false;
            this.canvas.toBlob((blob) => {
                this._blob = blob;
                if (blob) {
                    this.downloadButton.interactive = true;
                    console.log("Download ready");
                }
            });
        }
    }
    public static readonly Create = () => new AfterGame();
    public init(pSize: PIXI.Point) {
        super.init(pSize);
        PubSub.publishSync(HLF.CLEAR_FOCUS, undefined);
        PubSub.publishSync(HLF.PUSH_KEYBOARD_LAYER, undefined);
        Application.instance.captions.stopCaption();
    }

    public animateIn(pOncomplete: () => void) {
        Application.instance.voiceOver.play(
            HLF.ArrayUtils.random([VO.U5, VO.U6, VO.U7]),
            InteruptType.NONE,
            VoiceCategory.GAMEPLAY,
        );

        Application.instance.audio.play(SFX.CHEER_START, 1, false, HLF.AudioCategory.SFX);
        Application.instance.audio.play(SFX.PHOTO_APPEAR, 1, false, HLF.AudioCategory.SFX);

        new TimelineLite()
            // 0s->1.5s spin in photo container
            .add(TweenLite.from(this.photoContainer, 1.5, {pixi: {rotation: 45}, ease: Expo.easeOut}))
            // 0.25s->3.25s fade out zoom blur filter
            .add(TweenLite.to(this.filter, 3, {strength: 0, ease: Expo.easeOut}), 0.25)
            // 0.5s->1.5s scale in photo container
            .add(TweenLite.from(this.photoContainer, 1, {pixi: {scale: 1.5}, ease: Back.easeOut.config(1.6)}), 0.5)
            // 0.75s->1.75s fade in photo (on top of white bg)
            .add(TweenLite.from(this.photo, 1, {pixi: {alpha: 0}}), 0.75)
            // 1s->1.25s fade in bg
            .add(TweenLite.to(this._bg, 0.25, {pixi: {alpha: 1}}), 1)
            // 1.5s->2s scale in buttons
            .staggerFrom([this.printButton, this.downloadButton], 0.5, {
                    pixi: {
                        scale: 0,
                    },
                    ease: Back.easeOut.config(1.6),
                }, 0.25, 1.5)
            // 2.75s callbacks
            .call(() => {
                this.photo.filters = [];
                this.filter.enabled = false;
                PubSub.publishSync(HLF.REGISTER_FOCUSABLES, [this.printButton, this.downloadButton]);
                pOncomplete?.();
            })
            // 2.75s -> 3.25s scale in continue button after state transition is complete
            .from(this.continueButton, 0.5, {pixi: {scale: 0}, ease: Back.easeOut.config(1.6)})
            // 3s play second audio file
            .call(() => {
                Application.instance.audio.play(SFX.CHEER_LOOP, 1, true, HLF.AudioCategory.SFX);
            }, [], undefined, 3)
            // 3.25s register continue button for keyboard navigation
            .call(() => {
                PubSub.publishSync(HLF.REGISTER_FOCUSABLE, this.continueButton);
            })
        ;
    }
    public update(pDeltaTime: number) {
        super.update(pDeltaTime);
    }
    public onResize(pSize: PIXI.Point) {
        super.onResize(pSize);
        this.photo.filterArea = this.photo.getBounds();
        this.filter.center = new PIXI.Point((this.photo.filterArea.width / 2), (this.photo.filterArea.height / 2));
        this.photo.position.set(0, 0);
        this._bg.position.set(0, 0);
        this.continueButton.position.set(
            Math.min(this.photoContainer.width / 2, (pSize.x / 2) - (this.continueButton.width / 2)),
            this.photoContainer.height / 2,
        );
        this.printButton.position.set(
            this.printButton.width / 2 + 14,
            Math.min(
                this.photoContainer.height / 2 + this.printButton.height / 2,
                pSize.y / 2 - this.printButton.height / 2,
            ),
        );
        this.downloadButton.position.set(
            -this.downloadButton.width / 2 - 14,
            Math.min(
                this.photoContainer.height / 2 + this.downloadButton.height / 2,
                pSize.y / 2 - this.downloadButton.height / 2,
            ),
        );
    }
    public animateOut(pOncomplete: () => void) {
        TweenLite
            .to(this, 1, {pixi: {
                scale: 0,
                alpha: 0,
            }})
            .eventCallback(Constants.TWEEN_ONCOMPLETE, pOncomplete)
        ;
    }
    public destroy(pOptions?: boolean | PIXI.DestroyOptions) {
        super.destroy(pOptions);
        Application.instance.audio.stop(SFX.CHEER_LOOP, HLF.AudioCategory.SFX);
    }
    private print() {
        this.printButton.interactive = false;
        if (this.canvas === undefined) {
            this.canvas = this.prepareCanvas();
        }
        printJS({
            printable: this.canvas.toDataURL(),
            type: "image",
            documentTitle: "Luna-ConcertPhoto",
            onPrintDialogClose: () => this.printButton.interactive = true,
            onError: () => this.printButton.interactive = true,
        });
    }
    private download() {
        if (this._blob) {
            saveAs(this._blob, "Luna-ConcertPhoto.png");
        }
        else {
            console.warn("Download not ready yet");
        }
    }
    private continue() {
        this.continueButton.interactive = false;
        PubSub.publishSync(HLF.UNREGISTER_FOCUSABLES, [this.printButton, this.downloadButton, this.continueButton]);
        PubSub.publishSync(HLF.POP_KEYBOARD_LAYER, undefined);
        PubSub.publishSync(HLF.STATE_LOAD_STATE, new HLF.StateToken(
            Constants.STATE_GAME,
            Constants.LOAD_INTERSTITIAL,
            // HLF.TransitionStep.ShowLoadScreen,
            HLF.TransitionStep.LoadAssets,
            HLF.TransitionStep.AttachNewBehind,
            // HLF.TransitionStep.HideLoadScreen,
            HLF.TransitionStep.AnimCurrentOut,
            HLF.TransitionStep.AnimNewIn,
            HLF.TransitionStep.RemoveCurrent,
            HLF.TransitionStep.UnloadAssets,
            HLF.TransitionStep.UnloadUnusedAssets,
        ));
    }

    private prepareCanvas() {
        const container = new PIXI.Container();
        const bg = HLF.PixiUtils.makeSprite("luna_carmen-printable-horizontal-colour");
        const screenshot = new PIXI.Sprite(Game.screenshot);
        screenshot.anchor.set(0.5, 0.5);
        screenshot.scale.set(870 / 1024, 435 / 512);
        container.addChild(bg);
        container.addChild(screenshot);
        return Application.instance.renderer.extract.canvas(container);
    }

}
